import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ACTION_STATUSES,
  ACTION_STATUS_LABELS,
  PHASE_LABELS,
  type ActionItemView,
  type ActionStatus,
} from '@retro/shared';
import { ChevronRight, Plus } from 'lucide-react';
import { api, type RetroSummary } from '../lib/api.js';
import { useCurrentUser } from '../lib/auth.js';
import { useTeams } from '../lib/teams.js';
import { toast } from '../lib/toast.js';
import { AppShell } from '../components/AppShell.js';
import { Avatar, EmptyState, Modal, PageHead, Spinner, Stat } from '../components/ui.js';

type Tab = 'BOARD' | 'HISTORY' | 'MEMBERS';

export function TeamPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const { data: user } = useCurrentUser();
  const { teams, selectTeam } = useTeams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('BOARD');
  const [showInvite, setShowInvite] = useState(false);
  const [confirming, setConfirming] = useState<RetroSummary | null>(null);

  const teamQuery = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => api.teams.get(teamId!),
    enabled: Boolean(teamId),
  });

  const actionsQuery = useQuery({
    queryKey: ['team-actions', teamId],
    queryFn: () => api.teams.actions(teamId!),
    enabled: Boolean(teamId),
  });

  const retrosQuery = useQuery({
    queryKey: ['retros', teamId],
    queryFn: () => api.teams.retros(teamId!),
    enabled: Boolean(teamId),
  });

  // Keep the sidebar's Team select pointed at the team being viewed.
  useEffect(() => {
    if (teamId && teams.some((team) => team.id === teamId)) selectTeam(teamId);
  }, [teamId, teams, selectTeam]);

  const setStatus = useMutation({
    mutationFn: ({ actionItemId, status }: { actionItemId: string; status: ActionStatus }) =>
      api.teams.setActionStatus(teamId!, actionItemId, status),
    // Optimistic: dragging a card back because the server was slow feels broken.
    onMutate: async ({ actionItemId, status }) => {
      const key = ['team-actions', teamId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ actionItems: ActionItemView[] }>(key);
      queryClient.setQueryData<{ actionItems: ActionItemView[] }>(key, (current) =>
        current
          ? {
              actionItems: current.actionItems.map((item) =>
                item.id === actionItemId ? { ...item, status } : item,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['team-actions', teamId], context.previous);
      toast.error(error.message);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['team-actions', teamId] }),
  });

  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'FACILITATOR' | 'CONTRIBUTOR' }) =>
      api.teams.setRole(teamId!, userId, role),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.teams.removeMember(teamId!, userId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  const rotateInvite = useMutation({
    mutationFn: () => api.teams.rotateInvite(teamId!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  const stats = useMemo(() => {
    const actions = actionsQuery.data?.actionItems ?? [];
    const retros = retrosQuery.data?.retros ?? [];
    const open = actions.filter((a) => a.status !== 'DONE');
    const closed = retros.filter((r) => r.phase === 'CLOSED');
    const last = closed.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
    return { open, lastRetro: last ?? null, sessionsWithActions: new Set(open.map((a) => a.retroId)).size };
  }, [actionsQuery.data, retrosQuery.data]);

  const remove = useMutation({
    mutationFn: (retroId: string) => api.retros.remove(retroId),
    onSuccess: async () => {
      setConfirming(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['retros', teamId] }),
        queryClient.invalidateQueries({ queryKey: ['team-actions', teamId] }),
      ]);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!user || !teamId) return null;

  if (teamQuery.isLoading) {
    return (
      <AppShell user={user} crumb="Team hub">
        <Spinner label="Loading team…" />
      </AppShell>
    );
  }

  if (teamQuery.error) {
    return (
      <AppShell user={user} crumb="Team hub">
        <div className="card pad text-center">
          <p className="text-muted">{(teamQuery.error as Error).message}</p>
          <Link to="/" className="btn btn-ghost mt-4 inline-flex">
            Back to overview
          </Link>
        </div>
      </AppShell>
    );
  }

  const team = teamQuery.data!.team;
  const actions = actionsQuery.data?.actionItems ?? [];
  const isFacilitator = team.role === 'FACILITATOR';
  const inviteLink = team.inviteToken ? `${window.location.origin}/join/${team.inviteToken}` : null;
  const facilitatorCount = team.members.filter((member) => member.role === 'FACILITATOR').length;
  const openActionCount = actions.filter(
    (action) => action.retroId === confirming?.id && action.status !== 'DONE',
  ).length;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'BOARD', label: 'Action board' },
    { id: 'HISTORY', label: 'Session history' },
    { id: 'MEMBERS', label: 'Members' },
  ];

  return (
    <AppShell user={user} crumb="Team hub">
      <PageHead
        eyebrow="Team"
        title={team.name}
        lede={`${team.members.length} members · ${facilitatorCount} facilitator${facilitatorCount === 1 ? '' : 's'}`}
        actions={
          isFacilitator && inviteLink ? (
            <button type="button" className="btn" onClick={() => setShowInvite(true)}>
              <Plus size={15} aria-hidden /> Add member
            </button>
          ) : undefined
        }
      />

      <div className="stats !mt-0">
        <Stat
          label="Open actions"
          value={stats.open.length}
          note={`across ${stats.sessionsWithActions} session${stats.sessionsWithActions === 1 ? '' : 's'}`}
        />
        <Stat
          label="Last retrospective"
          value={
            stats.lastRetro
              ? new Date(stats.lastRetro.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
              : '—'
          }
          note={stats.lastRetro?.name ?? 'None yet'}
        />
        <Stat
          label="Members"
          value={team.members.length}
          note={`${facilitatorCount} facilitator${facilitatorCount === 1 ? '' : 's'}`}
        />
      </div>

      <div className="tabs" role="tablist" aria-label="Team sections">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'BOARD' ? (
        actionsQuery.isLoading ? (
          <Spinner label="Loading actions…" />
        ) : actions.length === 0 ? (
          <div className="card">
            <EmptyState emoji="📋" title="No actions yet">
              Actions created during a retrospective land here so the team can follow them through.
            </EmptyState>
          </div>
        ) : (
          <div className="kanban">
            {ACTION_STATUSES.map((status) => {
              const column = actions.filter((action) => action.status === status);
              return (
                <section key={status} className="kcol">
                  <h3>
                    {ACTION_STATUS_LABELS[status]}
                    <span className="badge-ghost">{column.length}</span>
                  </h3>
                  {column.map((action) => (
                    <article key={action.id} className="kcard">
                      <p className={status === 'DONE' ? 'strike' : undefined}>{action.title}</p>
                      <div className="kmeta">
                        {action.assignee ? (
                          <>
                            <Avatar emoji={action.assignee.avatar} name={action.assignee.name} size="xs" />
                            <span>
                              {action.assignee.name}
                              {action.dueDate
                                ? ` · due ${new Date(action.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                                : ''}
                            </span>
                          </>
                        ) : null}
                      </div>
                      <div className="kmeta">
                        <span aria-hidden>↩</span>
                        <Link to={`/retro/${action.retroId}`} className="hover:underline">
                          From {action.retroName}
                        </Link>
                      </div>
                      <label className="field-l">
                        Status
                        <select
                          className="input input-compact"
                          value={action.status}
                          aria-label={`Status for ${action.title}`}
                          onChange={(event) =>
                            setStatus.mutate({
                              actionItemId: action.id,
                              status: event.target.value as ActionStatus,
                            })
                          }
                        >
                          {ACTION_STATUSES.map((candidate) => (
                            <option key={candidate} value={candidate}>
                              {ACTION_STATUS_LABELS[candidate]}
                            </option>
                          ))}
                        </select>
                      </label>
                    </article>
                  ))}
                </section>
              );
            })}
          </div>
        )
      ) : null}

      {tab === 'HISTORY' ? (
        retrosQuery.isLoading ? (
          <Spinner label="Loading history…" />
        ) : (retrosQuery.data?.retros.length ?? 0) === 0 ? (
          <div className="card">
            <EmptyState emoji="🕰️" title="No retrospectives yet">
              Start one from the formats page and it will show up here afterwards.
            </EmptyState>
          </div>
        ) : (
          <div className="card">
            <ul className="list">
              {retrosQuery.data!.retros.map((retro) => (
                <li key={retro.id} className="li">
                  <span className="li-main">
                    <strong>{retro.name}</strong>
                    <small>
                      {new Date(retro.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                      {' · '}
                      {retro.templateName}
                      {' · '}
                      {retro.participantCount} people · {retro.commentCount} comments · {retro.actionItemCount} actions
                    </small>
                  </span>
                  <span className={clsx('badge', retro.phase === 'CLOSED' ? 'badge-ghost' : 'badge-accent')}>
                    {PHASE_LABELS[retro.phase]}
                  </span>
                  <Link to={`/retro/${retro.id}`} className="btn btn-sm">
                    Open summary <ChevronRight size={13} aria-hidden />
                  </Link>
                  {isFacilitator && retro.phase === 'CLOSED' ? (
                    <div className="mt-2 flex justify-end border-t border-black/5 pt-2">
                      <button
                        type="button"
                        className="btn-quiet text-sm text-rose-700"
                        // Named, not just "Delete": the history is a grid of
                        // cards, so an unqualified label is ambiguous to a
                        // screen reader and to Playwright.
                        aria-label={`Delete ${retro.name}`}
                        onClick={() => setConfirming(retro)}
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )
      ) : null}

      {tab === 'MEMBERS' ? (
        <div className="card">
          <ul className="list">
            {team.members.map((member) => (
              <li key={member.userId} className="li">
                <Avatar emoji={member.avatar} name={member.name} />
                <span className="li-main">
                  <strong>
                    {member.name}
                    {member.userId === user.id ? ' (you)' : ''}
                  </strong>
                  <small>{member.email}</small>
                </span>
                {isFacilitator ? (
                  <>
                    <select
                      className="input input-compact"
                      value={member.role}
                      onChange={(event) =>
                        setRole.mutate({
                          userId: member.userId,
                          role: event.target.value as 'FACILITATOR' | 'CONTRIBUTOR',
                        })
                      }
                      aria-label={`Role for ${member.name}`}
                    >
                      <option value="FACILITATOR">Facilitator</option>
                      <option value="CONTRIBUTOR">Contributor</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger-text"
                      onClick={() => removeMember.mutate(member.userId)}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <span className="badge">{member.role === 'FACILITATOR' ? 'Facilitator' : 'Contributor'}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Modal open={showInvite} onClose={() => setShowInvite(false)} title="Invite to team" width="sm">
        <p>
          Anyone with this link can join {team.name} as a contributor and see its retrospective history.
        </p>
        <input
          readOnly
          value={inviteLink ?? ''}
          className="input font-mono text-xs"
          onFocus={(event) => event.target.select()}
          aria-label="Team invite link"
        />
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-primary flex-1"
            onClick={() => {
              if (inviteLink) void navigator.clipboard.writeText(inviteLink).catch(() => toast.error('Could not copy.'));
            }}
          >
            Copy link
          </button>
          <button
            type="button"
            className="btn"
            // Rotation invalidates the current link with no further warning, so
            // a plain confirmation is the right amount of friction.
            onClick={() => {
              if (window.confirm('Generate a new invite link? The old link will stop working immediately.')) {
                rotateInvite.mutate();
              }
            }}
            disabled={rotateInvite.isPending}
          >
            {rotateInvite.isPending ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </Modal>
      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={`Delete “${confirming?.name ?? ''}”?`}
        width="sm"
      >
        <p className="text-sm text-ink/70">
          This removes the retrospective and{' '}
          <strong>
            its {confirming?.commentCount ?? 0} comments, its votes and its ROTI feedback
          </strong>
          . It cannot be undone.
        </p>
        {openActionCount > 0 ? (
          <p className="mt-2 text-sm text-rose-700">
            {openActionCount} action{openActionCount === 1 ? '' : 's'} the team is still tracking
            will disappear from the action board.
          </p>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn btn-danger flex-1"
            onClick={() => confirming && remove.mutate(confirming.id)}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Deleting…' : 'Delete retrospective'}
          </button>
          <button type="button" className="btn" onClick={() => setConfirming(null)}>
            Cancel
          </button>
        </div>
      </Modal>
    </AppShell>
  );
}
